import React from 'react';
import './ContentPolicyPage.css';

/**
 * Public content policy page.
 * Reviewers can access it at:
 * https://alekhineai.com/#/content-policy
 */
const ContentPolicyPage = () => {
    const EFFECTIVE_DATE = 'January 2026';

    return (
        <div className="cp-page">
            <header className="cp-hero">
                <div className="cp-hero-inner">
                    <div className="cp-title">Alekhine AI — Content Policy & Acceptable Use</div>
                    <div className="cp-subtitle">
                        Effective date: <strong>{EFFECTIVE_DATE}</strong>
                    </div>
                    <div className="cp-subtitle">
                        Alekhine AI is a creative filmmaking platform for generating structured story assets (screenplays, scenes,
                        images, audio, and videos) for fictional storytelling and visualization.
                    </div>

                    <div className="cp-cta-row">
                        <a className="cp-link" href="mailto:safety@alekhineai.com">Report safety issue</a>
                        <span className="cp-dot">•</span>
                        <a className="cp-link" href="mailto:appeals@alekhineai.com">Appeal a decision</a>
                        <span className="cp-dot">•</span>
                        <a className="cp-link" href="/#/login">Back to login</a>
                    </div>
                </div>
            </header>

            <main className="cp-content">
                <section className="cp-card">
                    <h2>1) Scope</h2>
                    <p>This policy applies to any content that is:</p>
                    <ul>
                        <li>Entered by users (prompts, scripts, descriptions, uploads)</li>
                        <li>Generated by Alekhine AI (text, images, audio, video)</li>
                        <li>Stored or shared through Alekhine AI (projects, exports, links, thumbnails)</li>
                    </ul>
                    <p>If you use Alekhine AI, you agree to comply with this policy and all applicable laws.</p>
                </section>

                <section className="cp-card">
                    <h2>2) Core principles</h2>
                    <p>Alekhine AI is built for <strong>creative, project-based filmmaking</strong>, not open public broadcasting.</p>
                    <ul>
                        <li><strong>Layered safeguards</strong> (pre-generation checks + post-generation checks)</li>
                        <li><strong>User accountability</strong> (authenticated access; actions tied to accounts/projects)</li>
                        <li><strong>Human oversight</strong> (users review outputs before exporting/sharing; Alekhine AI can review flagged items)</li>
                        <li><strong>Privacy-first defaults</strong> (content is private by default unless the user chooses to share)</li>
                        <li><strong>Strict prohibitions on high-risk content</strong>, especially involving minors</li>
                    </ul>
                </section>

                <section className="cp-card">
                    <h2>3) Age eligibility</h2>
                    <ul>
                        <li>Alekhine AI is intended for general audiences, but is <strong>not directed at children</strong>.</li>
                        <li>Users must meet the minimum age required by law in their jurisdiction to use online services.</li>
                        <li>If we become aware an account is operated in violation of age requirements, we may restrict or terminate access.</li>
                    </ul>
                </section>

                <section className="cp-card">
                    <h2>4) Prohibited content</h2>

                    <h3>A) Child sexual content (zero tolerance)</h3>
                    <p>
                        Any sexual content involving minors is strictly prohibited, including sexualization of minors, nudity or
                        suggestive depictions of minors, or “teen” sexual content where age is ambiguous or underage-coded.
                    </p>
                    <p className="cp-callout">
                        Enforcement: immediate removal and account termination. We may report to appropriate authorities where required.
                    </p>

                    <h3>B) Sexual content</h3>
                    <ul>
                        <li><strong>Not allowed:</strong> explicit sexual content, non-consensual sexual content, sexual violence, or exploitative sexual content.</li>
                        <li><strong>Allowed (contextual):</strong> non-explicit romance (e.g., affection, kissing) and non-graphic references in a screenplay.</li>
                    </ul>

                    <h3>C) Extreme violence or gore</h3>
                    <ul>
                        <li><strong>Not allowed:</strong> graphic gore, dismemberment, or extreme injury detail; torture/cruelty presented as celebratory or fetishized.</li>
                        <li><strong>Allowed (contextual):</strong> mainstream cinematic action/suspense without graphic detail (e.g., chases, implied injury).</li>
                    </ul>

                    <h3>D) Self-harm or suicide content</h3>
                    <ul>
                        <li><strong>Not allowed:</strong> encouragement, instructions, methods, or glorification of self-harm.</li>
                        <li><strong>Allowed (contextual):</strong> responsible fictional storytelling referencing crisis themes without instructions.</li>
                    </ul>

                    <h3>E) Hate or harassment</h3>
                    <p>
                        Not allowed: hate content targeting protected groups, threats, bullying, slurs, dehumanizing stereotypes, or encouragement of violence.
                    </p>

                    <h3>F) Illegal wrongdoing and instructions</h3>
                    <p>
                        Not allowed: instructions or facilitation for wrongdoing (weapons, fraud/scams, drug manufacturing, evasion, exploitation).
                    </p>

                    <h3>G) Extremism and terrorism</h3>
                    <p>
                        Not allowed: praise/support/propaganda/recruitment or instructional content for extremist or terrorist organizations.
                    </p>

                    <h3>H) Privacy violations and personal data</h3>
                    <p>
                        Not allowed: doxxing, requests for personal data, non-consensual intimate imagery, attempts to identify or track private individuals.
                    </p>

                    <h3>I) Impersonation and deception</h3>
                    <p>
                        Not allowed: impersonation for deception, deepfakes intended to mislead (fraud/harassment/political manipulation), false endorsement.
                    </p>

                    <h3>J) Intellectual property & rights</h3>
                    <p>
                        Not allowed: infringing content. Users must have rights/permission to upload or generate content based on third-party IP.
                    </p>

                    <h3>K) Real people, celebrities, and public figures</h3>
                    <p>
                        Alekhine AI is intended for <strong>fictional storytelling and original creative works</strong>.
                    </p>

                    <p><strong>Not allowed:</strong></p>
                    <ul>
                        <li>
                            Generating or depicting real individuals (including celebrities, public figures, or private individuals)
                            in sexual, pornographic, or exploitative contexts
                        </li>
                        <li>
                            Non-consensual, misleading, or deceptive depictions of real people
                        </li>
                        <li>
                            Deepfake-style content intended to impersonate, defame, deceive, or mislead others
                        </li>
                        <li>
                            Content that falsely represents a real person as endorsing actions, products, or viewpoints
                        </li>
                        <li>
                            Political persuasion or advocacy using the likeness or identity of real individuals
                        </li>
                    </ul>

                    <p><strong>Allowed (contextual and non-deceptive):</strong></p>
                    <ul>
                        <li>
                            Referencing well-known public figures in clearly fictional, narrative, or historical contexts,
                            where there is no sexualization, impersonation, or intent to mislead
                        </li>
                        <li>
                            Transformative or fictionalized characters that are inspired by real-world archetypes
                            but do not portray the individual as the real person
                        </li>
                    </ul>

                    <p>
                        Alekhine AI may block, restrict, or subject requests involving real individuals to additional
                        review where there is a risk of impersonation, deception, or misuse.
                    </p>

                </section>

                <section className="cp-card">
                    <h2>5) Safety guardrails and moderation</h2>

                    <h3>A) Pre-generation checks</h3>
                    <ul>
                        <li>We may scan prompts and project inputs for prohibited content.</li>
                        <li>Requests that appear to violate policy may be blocked, refused, or require revision.</li>
                    </ul>

                    <h3>B) Post-generation checks</h3>
                    <ul>
                        <li>Generated text/images/videos may be scanned and flagged if they resemble disallowed content.</li>
                        <li>If flagged, content may be suppressed, replaced, regenerated, or queued for review.</li>
                    </ul>

                    <h3>C) Human oversight</h3>
                    <ul>
                        <li>Users are expected to review outputs before export or sharing.</li>
                        <li>Alekhine AI may perform human review for flagged, reported, or suspicious activity.</li>
                    </ul>

                    <h3>D) Abuse prevention</h3>
                    <ul>
                        <li>Account-level throttling and usage limits</li>
                        <li>Project-based constraints</li>
                        <li>Temporary holds during anomalous usage patterns</li>
                        <li>Restrictions on certain capabilities for new or high-risk accounts</li>
                    </ul>
                </section>

                <section className="cp-card">
                    <h2>6) Reporting and enforcement</h2>

                    <h3>Reporting</h3>
                    <p>
                        To report content or behavior that violates this policy, email{' '}
                        <a className="cp-link" href="mailto:safety@alekhineai.com">safety@alekhineai.com</a>.
                        Include a project identifier (if applicable), timestamps, and a brief description.
                    </p>

                    <h3>Enforcement actions</h3>
                    <p>We may take one or more of the following actions:</p>
                    <ul>
                        <li>Warn the user and require changes</li>
                        <li>Block a prompt or generation request</li>
                        <li>Remove or suppress generated assets</li>
                        <li>Restrict features temporarily (e.g., video generation)</li>
                        <li>Suspend or terminate accounts for repeat or severe violations</li>
                    </ul>

                    <h3>Appeals</h3>
                    <p>
                        If you believe enforcement was applied in error, you may request an appeal by emailing{' '}
                        <a className="cp-link" href="mailto:appeals@alekhineai.com">appeals@alekhineai.com</a>.
                    </p>
                </section>

                <section className="cp-card">
                    <h2>7) User responsibilities</h2>
                    <p>You agree that you will:</p>
                    <ul>
                        <li>Use Alekhine AI for lawful, creative purposes</li>
                        <li>Not attempt to generate or distribute prohibited content</li>
                        <li>Not upload content you do not have the rights to use</li>
                        <li>Review outputs before sharing publicly</li>
                        <li>Comply with applicable platform rules when exporting content elsewhere</li>
                    </ul>
                </section>

                <section className="cp-card">
                    <h2>8) Creative context and false positives</h2>
                    <p>
                        Cinematic storytelling often includes fictional conflict, suspense, and action. Alekhine AI maintains strict
                        prohibitions on content involving minors, sexual exploitation, hate, self-harm encouragement, extremism, and
                        illegal instruction.
                    </p>
                    <p>
                        Where automated systems produce false positives for mainstream film scenarios (e.g., non-graphic action
                        sequences), Alekhine AI aims to resolve these via context-aware moderation and user review—without relaxing
                        protections for high-risk content.
                    </p>
                </section>

                <section className="cp-card">
                    <h2>9) Contact</h2>
                    <ul>
                        <li>Safety & policy: <a className="cp-link" href="mailto:safety@alekhineai.com">safety@alekhineai.com</a></li>
                        <li>Support: <a className="cp-link" href="mailto:support@alekhineai.com">support@alekhineai.com</a></li>
                    </ul>
                </section>
            </main>
        </div>
    );
};

export default ContentPolicyPage;
